import React, { useMemo, useState } from "react";
import {
  CircularProgress,
  FormControl,
  Select,
  InputLabel,
  FormHelperText,
  InputLabelProps,
  InputBaseComponentProps,
  MenuItem,
  useTheme,
  Tooltip,
  Typography,
  SelectProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import TooltipDialogCst from "@components/TooltipDialogCst";
import { Options, SelectComponentInterface } from "./types";
// import StaticOptions from './options.json';

import { useFetchOptions } from "@hooks/useFetchOptions";

const SCROLL_END_THRESHOLD = 24;
const SCROLL_END_MENU_MAX_HEIGHT = "300px";

const SelectCst: React.FC<
  SelectProps & InputBaseComponentProps & SelectComponentInterface
> = (props) => {
  const { t } = useTranslation(["input"]);

  const {
    id,
    label,
    value = "",
    placeholder = t("generic-placeholder") + label,
    onChange,
    options = undefined,
    extraOptions = [],
    optionsToDisable,
    customWidth,
    error = false,
    errorMessage = "",
    disabled,
    textTooltip,
    optionsKey,
    service,
    queryParams,
    required,
    sx,
    isFilterActive,
    endAdornment,
    testIdPrefix,
    ariaLabelOverride,
    optionTestId,
    optionAriaLabel,
    disableOptionEllipsis = false,
    size = "small",
    onScrollEnd,
    loading,
  } = props;

  const labelTestId = testIdPrefix ? `${testIdPrefix}-label` : undefined;
  const selectTestId = testIdPrefix ? `${testIdPrefix}-select` : undefined;
  const selectInputId = testIdPrefix ? `${testIdPrefix}-input` : id;
  const selectLabelId = testIdPrefix ? `${testIdPrefix}-label` : `label-${id}`;

  const theme = useTheme();

  const { getOptions } = useFetchOptions(
    service,
    optionsKey,
    options as string[] | undefined,
    queryParams,
  );

  return (
    <FormControl
      size={size}
      required={required}
      sx={{
        minWidth: "200px",
        display: "flex",
        flexDirection: "column",
        // alignItems: "center",
        width: customWidth ? customWidth : "auto",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: isFilterActive ? theme.palette.text.primary : "",
        },
        ...sx,
      }}
    >
      <InputLabel
        id={selectLabelId}
        error={error}
        sx={{
          opacity: disabled == true ? "0.4" : "",
        }}
        {...(labelTestId
          ? {
              "data-testid": labelTestId,
              ...(ariaLabelOverride ? { "aria-label": ariaLabelOverride } : {}),
            }
          : {})}
      >
        {label}
      </InputLabel>
      <Select
        error={error}
        labelId={selectLabelId}
        id={selectInputId}
        sx={{ width: "100%" }}
        placeholder={placeholder}
        inputProps={selectTestId ? { "data-testid": selectTestId } : undefined}
        value={
          ![null, undefined, "undefined"].includes(value)
            ? value || `${value}`
            : ""
        }
        onChange={(e: any) => {
          onChange && onChange(e.target.value);
        }}
        endAdornment={endAdornment}
        label={label}
        disabled={disabled}
        {...(onScrollEnd
          ? {
              MenuProps: {
                PaperProps: {
                  sx: { maxHeight: SCROLL_END_MENU_MAX_HEIGHT },
                  onScroll: (event: React.UIEvent<HTMLElement>) => {
                    const { scrollTop, clientHeight, scrollHeight } =
                      event.currentTarget;
                    if (
                      scrollTop + clientHeight >=
                      scrollHeight - SCROLL_END_THRESHOLD
                    ) {
                      onScrollEnd();
                    }
                  },
                },
              },
            }
          : {})}
      >
        {[...(getOptions() || []), ...extraOptions]?.map(
          (option: Options | any, index: number) => {
            const customAttrs = optionTestId?.(option);
            const customAria = optionAriaLabel?.(option);
            return (
              <MenuItem
                sx={{ whiteSpace: "normal" }}
                title={option?.label || option?.name}
                disabled={
                  optionsToDisable &&
                  optionsToDisable.includes(option?.value || option?.id)
                }
                key={index}
                value={option?.value || option?.id}
                {...(customAttrs?.id ? { id: customAttrs.id } : {})}
                {...(customAttrs?.testId
                  ? { "data-testid": customAttrs.testId }
                  : {})}
                {...(customAria ? { "aria-label": customAria } : {})}
              >
                <Typography
                  sx={{
                    maxWidth: disableOptionEllipsis ? "none" : "400px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option?.label || option?.name}
                </Typography>
              </MenuItem>
            );
          },
        )}
        {loading && (
          <MenuItem disabled sx={{ justifyContent: "center" }}>
            <CircularProgress size={14} />
          </MenuItem>
        )}
      </Select>
      {textTooltip && (
        <TooltipDialogCst inputDialog title={label} textTooltip={textTooltip} />
      )}
      {error && errorMessage && (
        <FormHelperText disabled={!error} error={error}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SelectCst;
